import {omit} from 'lodash-es';

import type {
  AfriexFeature,
  AfriexFeatureField,
  AfriexFeatureLimit,
} from '@/types';

import type {FeatureAction} from './types';
import {FeatureActionType} from './types';

export const TAG_OPERATOR_DELIMITER =
  /_EQUALS_|_NOT_EQUALS_|_BETWEEN_|_GREATER_THAN_|_LESS_THAN_/;

const FEATURE_TAG_REGEX =
  /^(CURRENCY|COUNTRY|USERID|REGION|EMAIL|GROUP|ROLE|OS|IP|SIGNUP_DATE|TRANSACTION_COUNT|TRANSACTION_VOLUME)_(.+)$/;

export function parseFeatureTag(tag: string) {
  const match = tag.match(FEATURE_TAG_REGEX);
  if (!match) {
    return null;
  }

  const [, field, operator, value] = match;
  return {field, operator, value};
}

const updateFeatureReducer = (state: AfriexFeature, action: FeatureAction) => {
  switch (action.type) {
    case FeatureActionType.UPDATE_FEATURE:
      return {
        ...state,
        ...action.payload,
      };

    case FeatureActionType.UPDATE_TAG: {
      const tagKey = Object.keys(action.payload)?.[0] as AfriexFeatureField;
      if (!tagKey) {
        return state;
      }
      return {
        ...state,
        meta: {
          ...(state.meta ?? {}),
          tags: {
            ...(state?.meta?.tags ?? {}),
            ...action.payload,
          },
        },
      };
    }

    case FeatureActionType.REMOVE_TAG:
      return {
        ...state,
        meta: {
          ...state?.meta,
          tags: omit(state?.meta?.tags ?? {}, [action.payload]),
        },
      };

    case FeatureActionType.ADD_TAG: {
      if (!action.payload) {
        return state;
      }
      const operatorAndValue = Object.values(action.payload)?.[0];
      const [operator, value] = operatorAndValue.split('::');
      if (operator === 'BETWEEN') {
        const values = (value || '')?.split(',');
        if (values?.length !== 2) {
          return state;
        }
      }

      return {
        ...state,
        meta: {
          ...state?.meta,
          tags: {
            ...(state?.meta?.tags ?? {}),
            ...action.payload,
          },
        },
      };
    }

    case FeatureActionType.UPDATE_LIMIT:
      return {
        ...state,
        meta: {
          ...(state?.meta ?? {}),
          // `action.payload` is the draft/in-progress limit (may be
          // incomplete while the user is still filling in the Limit form).
          // `state` itself is already treated as a draft throughout this
          // reducer (see the `initialData as AfriexFeature` cast in
          // useFeatureUpdate.ts); FeatureValidator enforces the real,
          // validated AfriexFeatureLimit shape before submission.
          limit: (action.payload || {}) as AfriexFeatureLimit,
        },
      };
    default:
      return state;
  }
};

export default updateFeatureReducer;
